/**
 * A TABELA DA RENOVAÇÃO EXPLICADA AO BKO (Douglas 22/10/2026, Maquete 3 aprovada:
 * "quando o bko escolher a tabela, ele explicar o que é a tabela e mandar ele dar
 * um ok de ciente antes de aprovar mesmo revisando").
 *
 * TABELA 1 = cliente que já tem fixa e móvel no CNPJ, ou não tem viabilidade no
 * endereço da Receita Federal, ou está com pedido de fibra junto e ela foi ativada
 * dentro do mesmo mês.
 * TABELA 2 = tem viabilidade, não tem fixa e não foi vendida fibra.
 */
export type MotivoTabela1 = 'fixa_e_movel' | 'sem_viabilidade' | 'fibra_junto_ativada';
export type ConfirmacaoTabela2 = 'tem_viabilidade' | 'sem_fixa' | 'sem_fibra_vendida';

export const MOTIVOS_TABELA_1: Array<{ id: MotivoTabela1; texto: string }> = [
  { id: 'fixa_e_movel', texto: 'já tem fixa e móvel no CNPJ' },
  { id: 'sem_viabilidade', texto: 'não tem viabilidade no endereço da Receita Federal' },
  { id: 'fibra_junto_ativada', texto: 'tem pedido de fibra junto e ela foi ativada no mesmo mês' },
];

export const CONFIRMACOES_TABELA_2: Array<{ id: ConfirmacaoTabela2; texto: string }> = [
  { id: 'tem_viabilidade', texto: 'tem viabilidade no endereço da Receita Federal' },
  { id: 'sem_fixa', texto: 'não tem fixa no CNPJ' },
  { id: 'sem_fibra_vendida', texto: 'não foi vendida fibra no pedido' },
];

export type FibraNoPedido = 'nao' | 'ativada_no_mes' | 'nao_ativou' | 'ativou_outro_mes';

export interface FatosTabela {
  /** null = o CRM não tem esse dado: o BKO confere no SOLAR. */
  fixaNoCnpj: boolean | null;
  viabilidade: boolean | null;
  fibraNoPedido: FibraNoPedido;
  mesRenovacao: string | null;
  /** Linhas da renovação por faixa (F1–F7), para a confirmação dizer o que muda. */
  linhasPorFaixa: Record<number, number>;
}

export interface ItemFatoTabela {
  produto: string | null;
  quadro_atual: string | null;
  data_ativacao: string | null;
  mes_trabalho: string | null;
  linhas_telefone?: unknown;
}

/** Negociação aberta e cancelado não contam como fibra vendida nem como linha renovada. */
const QUADROS_FORA = new Set(['COMERCIAL', 'PEDIDO_CANCELADO']);

const mesDe = (iso: string) => new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);

const linhas = (v: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(v)) return v.filter((l): l is Record<string, unknown> => !!l && typeof l === 'object');
  if (typeof v !== 'string' || !v.trim()) return [];
  try {
    const p = JSON.parse(v);
    return Array.isArray(p) ? p.filter((l): l is Record<string, unknown> => !!l && typeof l === 'object') : [];
  } catch {
    return [];
  }
};

/** O que o CRM já sabe: a convergência registrada e os itens da família do pedido. */
export function fatosDaTabela(
  conv: { fixa_ja_existente?: unknown; tem_fibra?: boolean | null; cobertura_fibra?: boolean | null } | null | undefined,
  itens: ItemFatoTabela[],
  produtosFibra: Set<string>,
): FatosTabela {
  const vivos = itens.filter((i) => !QUADROS_FORA.has(String(i.quadro_atual || '')));
  const renovacoes = vivos.filter((i) => String(i.produto || '').toUpperCase().startsWith('RENOVA'));
  const mesRenovacao = renovacoes.map((i) => i.mes_trabalho || '').filter(Boolean).sort().pop() || null;
  const fibras = vivos.filter((i) => produtosFibra.has(String(i.produto || '')));
  let fibraNoPedido: FibraNoPedido = 'nao';
  if (fibras.length > 0) {
    const ativadas = fibras.filter((f) => f.quadro_atual === 'QUALIDADE' && f.data_ativacao);
    if (ativadas.some((f) => mesRenovacao && mesDe(String(f.data_ativacao)) === mesRenovacao)) fibraNoPedido = 'ativada_no_mes';
    else if (ativadas.length > 0) fibraNoPedido = 'ativou_outro_mes';
    else fibraNoPedido = 'nao_ativou';
  }
  const linhasPorFaixa: Record<number, number> = {};
  for (const r of renovacoes) {
    for (const l of linhas(r.linhas_telefone)) {
      const f = Math.trunc(Number(l.faixa ?? l.faixa_apurada) || 0);
      if (f >= 1 && f <= 7) linhasPorFaixa[f] = (linhasPorFaixa[f] || 0) + 1;
    }
  }
  return {
    fixaNoCnpj: conv?.fixa_ja_existente ? true : conv?.tem_fibra === false ? false : null,
    viabilidade: conv?.cobertura_fibra === true ? true : conv?.cobertura_fibra === false ? false : null,
    fibraNoPedido,
    mesRenovacao,
    linhasPorFaixa,
  };
}

export interface EscolhaTabela {
  tabela: '1' | '2' | null;
  motivo1: MotivoTabela1 | null;
  confirmados2: ConfirmacaoTabela2[];
  ciente: boolean;
  conclusao: string;
}

/** Por que o motivo da Tabela 1 está fechado (null = pode escolher). */
export function bloqueioMotivo1(id: MotivoTabela1, fatos: FatosTabela): string | null {
  if (id !== 'fibra_junto_ativada') return null;
  if (fatos.fibraNoPedido === 'nao') return 'não há fibra neste pedido';
  if (fatos.fibraNoPedido === 'nao_ativou') return 'a fibra ainda não ativou: espere a ativação';
  if (fatos.fibraNoPedido === 'ativou_outro_mes') return 'a fibra ativou em outro mês: não vale';
  return null;
}

/** O que falta para gravar (null = pode gravar). */
export function faltaParaGravar(e: EscolhaTabela, fatos: FatosTabela): string | null {
  if (!e.tabela) return 'Escolha a Tabela 1 ou a Tabela 2.';
  if (e.tabela === '1') {
    if (!e.motivo1) return 'Marque o motivo da Tabela 1.';
    const b = bloqueioMotivo1(e.motivo1, fatos);
    if (b) return `Esse motivo não vale: ${b}.`;
  } else {
    if (fatos.fibraNoPedido === 'ativada_no_mes') return 'A fibra deste pedido ativou no mesmo mês: é Tabela 1.';
    if (CONFIRMACOES_TABELA_2.some((c) => !e.confirmados2.includes(c.id))) return 'Confirme os três pontos da Tabela 2.';
  }
  if (!e.conclusao.trim()) return 'Escreva a conclusão da consulta.';
  if (!e.ciente) return 'Marque que está ciente.';
  return null;
}

/** O texto que vai para a timeline: a tabela e o motivo. */
export function rotuloDecisaoTabela(e: Pick<EscolhaTabela, 'tabela' | 'motivo1'>): string {
  if (e.tabela === '1') {
    const m = MOTIVOS_TABELA_1.find((x) => x.id === e.motivo1);
    return `CONVERGENTE · TABELA 1 · ${m ? m.texto : 'sem motivo'}`;
  }
  return 'SÓ MÓVEL · TABELA 2 · tem viabilidade, não tem fixa e não foi vendida fibra';
}

export function resumoFaixas(linhasPorFaixa: Record<number, number>): string {
  const partes = Object.entries(linhasPorFaixa).sort(([a], [b]) => Number(a) - Number(b)).map(([f, n]) => `F${f} × ${n}`);
  return partes.length ? partes.join(' · ') : 'linhas sem faixa apurada';
}
